import React, { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Chip,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { foodAnalyzerService, RecognizeEventResponse, NutritionInfo } from '../../services/food-analyzer.service';
import { CalendarEventDto } from '@/types/calendar';
import { toastService } from '../../services/toast.service';
import { localNowIso, toLocalIso } from '@/utils/datetime';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { SchedulingBlock } from '../calendar/add-event-modals/SchedulingBlock';
import { eventColors } from '@/styles/tokens';

const FOOD_ACCENT       = eventColors.food.main;
const FOOD_ACCENT_HOVER = eventColors.food.dark;

type WizardStep = 'input' | 'recognized' | 'estimated';

interface QuickFoodAddModalV2Props {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  initialScheduledAt?: Date;
  onRefreshDashboard?: () => void;
  clientId?: string;
  /** Якщо передано — модаль відкривається в режимі редагування AI-події */
  eventToEdit?: CalendarEventDto | null;
}

const calculateItemNutrients = (amount: number | undefined, nutrition: NutritionInfo | undefined) => {
  if (!nutrition || amount === undefined) return null;
  const factor = amount / 100;
  return {
    kcal: (nutrition.caloriesPer100 * factor).toFixed(0),
    p:    (nutrition.proteinPer100  * factor).toFixed(1),
    f:    (nutrition.fatPer100      * factor).toFixed(1),
    c:    (nutrition.carbsPer100    * factor).toFixed(1),
    fiber:(nutrition.fiberPer100    * factor).toFixed(1),
  };
};

export const QuickFoodAddModalV2: React.FC<QuickFoodAddModalV2Props> = ({
  open,
  onClose,
  selectedDate,
  initialScheduledAt,
  onRefreshDashboard,
  clientId,
  eventToEdit,
}) => {
  const isEditMode = !!eventToEdit;

  const [step, setStep]                       = useState<WizardStep>('input');
  const [inputText, setInputText]             = useState('');
  const [isRecognizing, setIsRecognizing]     = useState(false);
  const [isEstimating, setIsEstimating]       = useState(false);
  const [isSaving, setIsSaving]               = useState(false);
  const inputRef                              = useRef<HTMLInputElement>(null);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeEventResponse[]>([]);
  const [suggestedTitle, setSuggestedTitle]   = useState('');
  const [editingIndex, setEditingIndex]       = useState<number | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(!!initialScheduledAt);
  const [scheduledAt, setScheduledAt]         = useState<Date | null>(initialScheduledAt || null);
  const [isCompleted, setIsCompleted]         = useState(false);

  const isLoading = isRecognizing || isEstimating || isSaving;

  React.useEffect(() => {
    if (!open) return;
    if (eventToEdit) {
      const details = eventToEdit.details as any;
      setInputText(details?.rawInput ?? '');
      setSuggestedTitle(eventToEdit.title ?? '');
      const components: RecognizeEventResponse[] = (details?.components ?? []).map((c: any) => ({
        type:         c.type ?? 'food',
        name:         c.name ?? '',
        totalAmount:  c.totalAmount ?? 0,
        unit:         c.unit ?? 'г',
        portionName:  c.portionName ?? undefined,
        portionSize:  c.portionSize ?? undefined,
        portionCount: c.portionCount ?? undefined,
        nutrition:    c.nutrition ?? undefined,
        scheduledLdt: eventToEdit.scheduledLdt ?? localNowIso(),
      }));
      setRecognizedEvents(components);
      if (eventToEdit.scheduledLdt) {
        setScheduledAt(new Date(eventToEdit.scheduledLdt));
        setShowDateTimePicker(true);
      }
      setIsCompleted(eventToEdit.isCompleted ?? false);
      setStep('estimated');
    } else {
      setInputText('');
      setRecognizedEvents([]);
      setSuggestedTitle('');
      setEditingIndex(null);
      setShowDateTimePicker(!!initialScheduledAt);
      setScheduledAt(initialScheduledAt || null);
      setIsCompleted(false);
      setStep('input');
    }
  }, [open, eventToEdit]);

  const handleClose = () => {
    setInputText('');
    setRecognizedEvents([]);
    setSuggestedTitle('');
    setIsRecognizing(false);
    setIsEstimating(false);
    setIsSaving(false);
    setEditingIndex(null);
    setShowDateTimePicker(false);
    setScheduledAt(null);
    setIsCompleted(false);
    setStep('input');
    onClose();
  };

  // Combines selectedDate (dashboard date) with current local time → T2 string without Z
  const buildContextDate = () => `${selectedDate}T${localNowIso().slice(11)}`;

  const handleRecognize = async () => {
    if (!inputText.trim()) return;
    setIsRecognizing(true);
    setEditingIndex(null);
    try {
      const data = await foodAnalyzerService.recognize({
        text: inputText,
        contextDate: buildContextDate(),
      }, clientId);
      setRecognizedEvents(data.items);
      setSuggestedTitle(data.suggestedTitle || '');
      setStep('recognized');
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося розпізнати текст. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleEstimateNutrition = async () => {
    if (!recognizedEvents.length) return;
    setIsEstimating(true);
    try {
      const items = recognizedEvents.map(e => ({
        name: e.name || 'Без назви',
        totalAmount: Number(e.totalAmount) || 0,
        unit: e.unit || 'г',
      }));
      const data = await foodAnalyzerService.estimateNutrition({ items }, clientId);
      setRecognizedEvents(recognizedEvents.map((e, i) =>
        data[i]?.nutrition ? { ...e, nutrition: data[i].nutrition } : e
      ));
      setStep('estimated');
      toastService.success('Нутрієнти успішно розраховано');
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося розрахувати нутрієнти.');
    } finally {
      setIsEstimating(false);
    }
  };

  const handleSave = async () => {
    const scheduledLdt = showDateTimePicker && scheduledAt ? toLocalIso(scheduledAt) : buildContextDate();
    const finalIsCompleted = showDateTimePicker ? isCompleted : true;
    const successMsg = isEditMode ? 'Подію оновлено' : 'Їжу успішно додано';

    const callApi = (payload: Parameters<typeof foodAnalyzerService.createEventsComplex>[0]) =>
      isEditMode && eventToEdit
        ? foodAnalyzerService.updateEventsComplex(eventToEdit.id, payload)
        : foodAnalyzerService.createEventsComplex(payload, clientId);

    setIsSaving(true);
    try {
      if (step === 'input') {
        try {
          await callApi({ startFromStep: 1, text: inputText, scheduledLdt, rawInput: inputText, isCompleted: finalIsCompleted });
          toastService.success(successMsg);
          onRefreshDashboard?.();
          handleClose();
        } catch (error: any) {
          const errBody = error?.response?.data;
          const partial = errBody?.data;
          if (partial?.completedStep >= 1 && partial?.items?.length) {
            const fallbackLdt = partial.scheduledLdt || localNowIso();
            setRecognizedEvents(partial.items.map((item: any) => ({ ...item, scheduledLdt: item.scheduledLdt ?? fallbackLdt })));
            setSuggestedTitle(partial.suggestedTitle || '');
            setStep(partial.completedStep >= 2 ? 'estimated' : 'recognized');
          }
          const validationErrors = errBody?.errors;
          if (validationErrors?.length) {
            toastService.error(validationErrors.map((e: any) => e.message).join('\n'));
          } else {
            toastService.error(errBody?.message || 'Не вдалося зберегти. Спробуйте ще раз.');
          }
        }
        return;
      }

      const itemsToSave = recognizedEvents.filter(e => e.type === 'food' || e.type === 'liquid');
      if (!itemsToSave.length) { toastService.error('Немає позицій їжі для збереження.'); return; }
      await callApi({
        startFromStep: step === 'recognized' ? 2 : 3,
        items:         itemsToSave,
        scheduledLdt,
        title:         suggestedTitle || undefined,
        rawInput:      inputText || undefined,
        isCompleted:   finalIsCompleted,
      });
      toastService.success(successMsg);
      onRefreshDashboard?.();
      handleClose();
    } catch (error: any) {
      const errBody = error?.response?.data;
      const partial = errBody?.data;
      if (partial?.completedStep >= 1 && partial?.items?.length) {
        const fallbackLdt = partial.scheduledLdt || localNowIso();
        setRecognizedEvents(partial.items.map((item: any) => ({ ...item, scheduledLdt: item.scheduledLdt ?? fallbackLdt })));
        setSuggestedTitle(partial.suggestedTitle || '');
        setStep(partial.completedStep >= 2 ? 'estimated' : 'recognized');
      }
      const validationErrors = errBody?.errors;
      if (validationErrors?.length) {
        toastService.error(validationErrors.map((e: any) => e.message).join('\n'));
      } else {
        toastService.error(errBody?.message || 'Не вдалося зберегти. Спробуйте ще раз.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const totals = useMemo(() => recognizedEvents.reduce((acc, e) => {
    if (e.nutrition && e.totalAmount !== undefined) {
      const k = Number(e.totalAmount) / 100;
      acc.calories += (Number(e.nutrition.caloriesPer100) || 0) * k;
      acc.protein  += (Number(e.nutrition.proteinPer100)  || 0) * k;
      acc.fat      += (Number(e.nutrition.fatPer100)      || 0) * k;
      acc.carbs    += (Number(e.nutrition.carbsPer100)    || 0) * k;
      acc.fiber    += (Number(e.nutrition.fiberPer100)    || 0) * k;
    }
    return acc;
  }, { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0 }), [recognizedEvents]);

  const handleAddItemManual = () => {
    const newItem: RecognizeEventResponse = {
      type: 'food', name: '', unit: 'г',
      portionCount: 1, portionSize: 0, totalAmount: 0,
      scheduledLdt: localNowIso(),
    };
    const newList = [...recognizedEvents, newItem];
    setRecognizedEvents(newList);
    setEditingIndex(newList.length - 1);
  };

  const handleDeleteItem = (index: number) => {
    setRecognizedEvents(recognizedEvents.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'food':         return <RestaurantIcon sx={{ color: '#6EB52C' }} />;
      case 'liquid':       return <WaterDropIcon  sx={{ color: '#41AEE4' }} />;
      case 'workout':      return <FitnessCenterIcon sx={{ color: '#FF9800' }} />;
      case 'unrecognized': return <QuestionMarkIcon  sx={{ color: '#A8A8A8' }} />;
      default:             return <AutoFixHighIcon />;
    }
  };

  const formatTime = (isoString: string) => {
    try { return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeEventResponse, value: any) => {
    const updated = [...recognizedEvents];
    const numericFields: (keyof RecognizeEventResponse)[] = ['portionSize', 'portionCount', 'totalAmount'];
    const parsed = numericFields.includes(field) ? (value === '' ? 0 : Number(value)) : value;
    const item = { ...updated[index], [field]: parsed };
    if (field === 'portionSize' || field === 'portionCount') {
      item.totalAmount = Number(item.portionSize || 0) * Number(item.portionCount || 0);
    }
    updated[index] = item;
    setRecognizedEvents(updated);
  };

  // ─── Save button disabled rule ───────────────────────────────────────────────
  const hasSaveableItems = recognizedEvents.filter(e => e.type === 'food' || e.type === 'liquid').length > 0;
  const isSaveDisabled   = isLoading || editingIndex !== null || (
    step === 'input' ? !inputText.trim() : !hasSaveableItems
  );

  const saveButton = (
    <Button
      onClick={handleSave}
      variant="contained"
      disabled={isSaveDisabled}
      startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
      sx={{ bgcolor: FOOD_ACCENT, '&:hover': { bgcolor: FOOD_ACCENT_HOVER }, borderRadius: '10px', px: 3 }}
    >
      {isSaving ? 'Зберігаємо...' : 'Зберегти'}
    </Button>
  );

  // ─── DialogActions (step-based, однакова логіка для create і edit) ────────────
  const dialogActions = (() => {
    if (step === 'input') {
      return (
        <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Button
            onClick={handleRecognize}
            variant="outlined"
            disabled={isLoading || !inputText.trim()}
            startIcon={isRecognizing ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
            sx={{ borderRadius: '10px', color: FOOD_ACCENT, borderColor: FOOD_ACCENT }}
          >
            {isRecognizing ? 'Розпізнаємо...' : 'Розпізнати'}
          </Button>
          {saveButton}
        </DialogActions>
      );
    }

    if (step === 'recognized') {
      return (
        <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Button
            onClick={handleEstimateNutrition}
            variant="outlined"
            disabled={isLoading || !recognizedEvents.length}
            startIcon={isEstimating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
            sx={{ borderRadius: '10px', color: FOOD_ACCENT, borderColor: FOOD_ACCENT }}
          >
            {isEstimating ? 'Рахуємо...' : 'Розрахувати'}
          </Button>
          {saveButton}
        </DialogActions>
      );
    }

    // step === 'estimated'
    return (
      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        {saveButton}
      </DialogActions>
    );
  })();

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px', borderRadius: '16px' } }}
      data-i-doc={ModalIndices.QUICK_FOOD_ADD}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <FastfoodIcon sx={{ color: FOOD_ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Редагувати їжу' : 'Додати їжу'}
          </Typography>
        </Box>
        {(!isEditMode || step !== 'estimated') && (
          <Stepper
            activeStep={step === 'input' ? 0 : step === 'recognized' ? 1 : 2}
            sx={{ flex: 1, '& .MuiStep-root': { px: 1 }, '& .MuiStepLabel-root': { py: 0 } }}
          >
            {[0, 1, 2].map((i) => <Step key={i}><StepLabel /></Step>)}
          </Stepper>
        )}
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500], flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'action.hover', gap: 2 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          label="Що ви їли?"
          placeholder="Наприклад: вівсянка 200г з молоком і ягодами"
          multiline
          rows={recognizedEvents.length > 0 ? 2 : 6}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
              e.preventDefault();
              handleRecognize();
            }
          }}
          sx={{ bgcolor: 'background.paper', transition: 'all 0.3s ease', '& .MuiInputBase-root': { borderRadius: '12px' } }}
        />

        {suggestedTitle && (
          <TextField
            fullWidth
            label="Назва події"
            value={suggestedTitle}
            onChange={(e) => setSuggestedTitle(e.target.value)}
            sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
          />
        )}

        {recognizedEvents.length > 0 ? (
          <Fade in>
            <Box>
              <List disablePadding>
                {recognizedEvents.map((event, index) => {
                  const itemNutrition = calculateItemNutrients(Number(event.totalAmount), event.nutrition);
                  return (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        borderColor: editingIndex === index ? FOOD_ACCENT : '#e0e0e0',
                        borderWidth: editingIndex === index ? 2 : 1,
                      }}
                    >
                      <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                        {/* Item header row */}
                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                            {getEventIcon(event.type)}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            {editingIndex === index ? (
                              <TextField
                                fullWidth
                                label="Назва"
                                value={event.name}
                                onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                  {event.name || 'Без назви'}
                                </Typography>
                                {event.originalText && (
                                  <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                    <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                                <Tooltip title={`Час: ${formatTime(event.scheduledLdt)}`}>
                                  <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant={editingIndex === index ? 'contained' : 'text'}
                              color={editingIndex === index ? 'success' : 'inherit'}
                              onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                              startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                            >
                              {editingIndex === index ? 'Ок' : 'Змінити'}
                            </Button>
                            <IconButton size="small" color="error" onClick={() => handleDeleteItem(index)} sx={{ opacity: 0.7 }}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Item details */}
                        <Box sx={{ width: '100%', ml: editingIndex === index ? 0 : 5 }}>
                          {editingIndex === index ? (
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    <b>Тип:</b> {event.type === 'food' ? 'Їжа' : event.type === 'liquid' ? 'Напій' : 'Інше'}
                                  </Typography>
                                  <TextField size="small" label="Порція (назва)" value={event.portionName || ''} placeholder="бокал, тарілка..." onChange={(e) => handleUpdateEventField(index, 'portionName', e.target.value)} />
                                  <TextField size="small" type="number" label="К-сть в порції" value={event.portionSize || ''} onChange={(e) => handleUpdateEventField(index, 'portionSize', e.target.value)} />
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="body2"><b>Час:</b> {formatTime(event.scheduledLdt)}</Typography>
                                  <TextField size="small" type="number" label="К-сть порцій" value={event.portionCount || ''} onChange={(e) => handleUpdateEventField(index, 'portionCount', e.target.value)} />
                                  <Box sx={{ mt: 1, p: 1, bgcolor: '#E8F5E9', borderRadius: 1 }}>
                                    <Typography variant="body2"><b>Разом:</b> {event.totalAmount || 0} {event.unit}</Typography>
                                    {itemNutrition && (
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                        {itemNutrition.kcal} ккал | Б: {itemNutrition.p}г Ж: {itemNutrition.f}г В: {itemNutrition.c}г К: {itemNutrition.fiber}г
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: event.nutrition ? 1 : 0 }}>
                                {(event.type === 'food' || event.type === 'liquid') && (
                                  <>
                                    {(event.portionCount || event.portionName) && (
                                      <Chip
                                        label={`${event.portionCount || 1} ${event.portionName || 'порц.'} ${event.portionSize ? `(по ${event.portionSize} ${event.unit || ''})` : ''}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600, borderColor: event.type === 'food' ? '#6EB52C' : '#41AEE4', color: event.type === 'food' ? '#6EB52C' : '#41AEE4' }}
                                      />
                                    )}
                                    {event.totalAmount !== undefined && (
                                      <Chip
                                        label={`Разом: ${event.totalAmount} ${event.unit || ''}`}
                                        size="small"
                                        sx={{ fontWeight: 600, bgcolor: event.type === 'food' ? '#E8F5E9' : '#E3F2FD', color: event.type === 'food' ? '#2E7D32' : '#1976D2' }}
                                      />
                                    )}
                                    {itemNutrition && (
                                      <Chip label={`${itemNutrition.kcal} ккал`} size="small" sx={{ fontWeight: 700, bgcolor: '#6EB52C', color: '#fff' }} />
                                    )}
                                  </>
                                )}
                              </Box>
                              {itemNutrition && (
                                <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700, mb: 1 }}>
                                  Всього: Б: {itemNutrition.p}г | Ж: {itemNutrition.f}г | В: {itemNutrition.c}г | К: {itemNutrition.fiber}г
                                </Typography>
                              )}
                              {event.nutrition && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, bgcolor: '#f5f5f5', p: 0.8, borderRadius: '6px', borderLeft: '3px solid #ccc' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span style={{ fontSize: '0.7rem' }}>БАЗА (на 100г/мл):</span>
                                    <span><b>Кал:</b> {event.nutrition.caloriesPer100}</span>
                                    <span><b>Б:</b> {event.nutrition.proteinPer100}</span>
                                    <span><b>Ж:</b> {event.nutrition.fatPer100}</span>
                                    <span><b>В:</b> {event.nutrition.carbsPer100}</span>
                                    <span><b>К:</b> {event.nutrition.fiberPer100}</span>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    </Paper>
                  );
                })}
              </List>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    setRecognizedEvents([]);
                    setSuggestedTitle('');
                    setStep('input');
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                  sx={{ color: 'text.secondary', textDecoration: 'underline', fontSize: '0.75rem' }}
                >
                  Не те? Уточніть запит
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  onClick={handleAddItemManual}
                  sx={{ color: FOOD_ACCENT, fontWeight: 700 }}
                >
                  Додати страву вручну
                </Button>
              </Box>
            </Box>
          </Fade>
        ) : (
          !isLoading && inputText.trim() && step !== 'input' && (
            <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography color="textSecondary">Результати відсутні. Додайте страву вручну або спробуйте знову.</Typography>
              <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddItemManual} sx={{ color: FOOD_ACCENT, borderColor: FOOD_ACCENT }}>
                Додати страву вручну
              </Button>
            </Box>
          )
        )}

        {totals.calories > 0 && (
          <Fade in>
            <Paper sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: '12px', border: '1px solid #6EB52C' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#2E7D32', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Загальний підсумок:
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>{totals.calories.toFixed(0)}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>ккал</Typography>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {([
                      { label: 'Білки',      val: totals.protein, color: undefined },
                      { label: 'Жири',       val: totals.fat,     color: undefined },
                      { label: 'Вуглеводи',  val: totals.carbs,   color: undefined },
                      { label: 'Клітковина', val: totals.fiber,   color: '#2E7D32' },
                    ] as const).map(({ label, val, color }) =>{
                      return (
                        <Box key={label}>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1, ...(color ? { color } : {}) }}>{val.toFixed(1)}г</Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: color ?? 'text.secondary' }}>{label}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        <SchedulingBlock
          showDateTimePicker={showDateTimePicker}
          onShowDateTimePickerChange={setShowDateTimePicker}
          scheduledAt={scheduledAt}
          onScheduledAtChange={setScheduledAt}
          isCompleted={isCompleted}
          onIsCompletedChange={setIsCompleted}
          switchLabel="Встановити дату та час"
          switchSize="small"
          gap={1}
        />
      </DialogContent>

      {dialogActions}
    </Dialog>
  );
};
